// Sticky Notes — free-floating notes/images that live ON TOP of the whole app
// (Lissette 2026-07-12: "stick note para tener a la mano notas o imagenes que se
// puedan mover de sitio, cerrar, editar y agrandar"). Each note can be dragged
// anywhere, resized, recolored, edited, given an image, collapsed, or closed.
//
// Persistence is per-user in localStorage (a personal scratchpad, always at
// hand, device-local). Images are downscaled to keep well under the ~5MB quota.
// Mounted once globally in deskte-full so notes float over every screen.

import { useState, useRef, useEffect } from 'react';
import { useLocalStorage } from '../hooks.js';

const COLORS = [
  { id: 'yellow', bg: '#fef3c7', bar: '#fde68a', text: '#78350f' },
  { id: 'pink',   bg: '#fce7f3', bar: '#f9a8d4', text: '#831843' },
  { id: 'green',  bg: '#dcfce7', bar: '#86efac', text: '#14532d' },
  { id: 'blue',   bg: '#dbeafe', bar: '#93c5fd', text: '#1e3a8a' },
  { id: 'purple', bg: '#ede9fe', bar: '#c4b5fd', text: '#4c1d95' },
];
const colorOf = (id) => COLORS.find((c) => c.id === id) || COLORS[0];
const genId = () => 'n' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

// Read an image file and return a downscaled JPEG data URL so a few images
// don't blow the localStorage quota.
function fileToDataUrl(file, maxDim = 1000, quality = 0.72) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const s = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * s);
          height = Math.round(height * s);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        try { resolve(canvas.toDataURL('image/jpeg', quality)); }
        catch (e) { reject(e); }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function Note({ note, onChange, onClose, es }) {
  const c = colorOf(note.color);
  const [pos, setPos] = useState({ x: note.x, y: note.y });
  const [size, setSize] = useState({ w: note.w, h: note.h });
  const [colorOpen, setColorOpen] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { setPos({ x: note.x, y: note.y }); }, [note.x, note.y]);
  useEffect(() => { setSize({ w: note.w, h: note.h }); }, [note.w, note.h]);

  const startDrag = (e) => {
    if (e.target.closest('button') || e.target.closest('input')) return;
    const sx = e.clientX, sy = e.clientY, o = { ...pos };
    // Track the last position locally: calling onChange (parent setState) inside
    // a setPos updater is a side effect React may re-invoke, which loops into
    // "Maximum update depth exceeded" (Sentry DESKTE-SPA-1Y).
    let last = o;
    const move = (ev) => {
      const nx = Math.max(0, Math.min(window.innerWidth - 60, o.x + (ev.clientX - sx)));
      const ny = Math.max(0, Math.min(window.innerHeight - 44, o.y + (ev.clientY - sy)));
      last = { x: nx, y: ny };
      setPos(last);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      onChange({ x: last.x, y: last.y });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    e.preventDefault();
  };

  const startResize = (e) => {
    e.stopPropagation();
    const sx = e.clientX, sy = e.clientY, o = { ...size };
    // Same pattern as startDrag: keep onChange out of the setSize updater.
    let last = o;
    const move = (ev) => {
      last = {
        w: Math.max(190, o.w + (ev.clientX - sx)),
        h: Math.max(140, o.h + (ev.clientY - sy)),
      };
      setSize(last);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      onChange({ w: last.w, h: last.h });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    e.preventDefault();
  };

  const pickImage = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    try {
      const url = await fileToDataUrl(file);
      onChange({ image: url });
    } catch {
      alert(es ? 'No se pudo cargar la imagen.' : 'Could not load the image.');
    }
  };

  // Pegar una imagen copiada (Ctrl+V / Cmd+V) directamente en la nota: una
  // captura de pantalla, o una foto copiada desde otra pestaña, sin tener que
  // guardarla antes en el disco y buscarla con el botón 🖼️ (Lissette
  // 2026-08-25). El portapapeles trae la imagen como un File igual que el
  // <input type=file>, así que pasa por el MISMO fileToDataUrl y queda con el
  // mismo tamaño reducido — pegar no puede reventar la cuota de localStorage
  // por una ruta distinta. Si en el portapapeles no hay ninguna imagen se
  // deja pasar el evento y el texto se pega como siempre.
  const pasteImage = async (e) => {
    const items = e.clipboardData ? Array.from(e.clipboardData.items || []) : [];
    const imgItem = items.find((it) => it.kind === 'file' && String(it.type || '').startsWith('image/'));
    if (!imgItem) return;
    const file = imgItem.getAsFile();
    if (!file) return;
    e.preventDefault();
    try {
      const url = await fileToDataUrl(file);
      onChange({ image: url });
    } catch {
      alert(es ? 'No se pudo pegar la imagen.' : 'Could not paste the image.');
    }
  };

  const collapsed = !!note.collapsed;
  const btn = { background: 'rgba(0,0,0,.08)', border: 'none', borderRadius: 6, width: 22, height: 22, cursor: 'pointer', color: c.text, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, lineHeight: 1, flexShrink: 0 };

  return (
    <div
      onPaste={pasteImage}
      style={{
        position: 'fixed', left: pos.x, top: pos.y,
        width: size.w, height: collapsed ? 'auto' : size.h,
        background: c.bg, color: c.text,
        borderRadius: 10, boxShadow: '0 10px 30px rgba(0,0,0,.35)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        zIndex: 45, border: '1px solid rgba(0,0,0,.12)',
      }}
    >
      {/* Header / drag handle */}
      <div
        onPointerDown={startDrag}
        style={{ background: c.bar, padding: '5px 7px', display: 'flex', alignItems: 'center', gap: 5, cursor: 'grab', touchAction: 'none', flexShrink: 0 }}
      >
        <span style={{ fontSize: 13, flexShrink: 0 }}>📌</span>
        <div style={{ position: 'relative', display: 'inline-flex' }}>
          <button type="button" title={es ? 'Color' : 'Color'} onClick={() => setColorOpen((o) => !o)}
            style={{ ...btn, background: 'rgba(255,255,255,.5)' }}>🎨</button>
          {colorOpen && (
            <>
              <div onClick={() => setColorOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 1 }} />
              <div style={{ position: 'absolute', top: 26, left: 0, zIndex: 2, background: '#fff', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,.3)', padding: 5, display: 'flex', gap: 4 }}>
                {COLORS.map((col) => (
                  <button key={col.id} type="button" onClick={() => { onChange({ color: col.id }); setColorOpen(false); }}
                    style={{ width: 20, height: 20, borderRadius: '50%', background: col.bg, border: note.color === col.id ? '2px solid #333' : '1px solid rgba(0,0,0,.2)', cursor: 'pointer', flexShrink: 0 }} />
                ))}
              </div>
            </>
          )}
        </div>
        <button type="button" title={es ? 'Imagen (o pégala con Ctrl+V dentro de la nota)' : 'Image (or paste it with Ctrl+V inside the note)'} onClick={() => fileRef.current && fileRef.current.click()} style={btn}>🖼️</button>
        <input ref={fileRef} type="file" accept="image/*" onChange={pickImage} style={{ display: 'none' }} />
        <div style={{ flex: 1 }} />
        <button type="button" title={collapsed ? (es ? 'Expandir' : 'Expand') : (es ? 'Contraer' : 'Collapse')} onClick={() => onChange({ collapsed: !collapsed })} style={btn}>{collapsed ? '▢' : '—'}</button>
        <button type="button" title={es ? 'Cerrar' : 'Close'} onClick={onClose} style={{ ...btn, background: 'rgba(220,38,38,.15)', color: '#b91c1c', fontWeight: 700 }}>✕</button>
      </div>

      {!collapsed && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {note.image && (
            <div style={{ position: 'relative', flexShrink: 0, maxHeight: '55%', overflow: 'hidden', borderBottom: '1px solid rgba(0,0,0,.08)' }}>
              <img src={note.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', maxHeight: 220 }} />
              <button type="button" title={es ? 'Quitar imagen' : 'Remove image'} onClick={() => onChange({ image: null })}
                style={{ position: 'absolute', top: 4, right: 4, ...btn, background: 'rgba(0,0,0,.45)', color: '#fff' }}>✕</button>
            </div>
          )}
          <textarea
            value={note.text || ''}
            onChange={(e) => onChange({ text: e.target.value })}
            placeholder={es ? 'Escribe una nota… (o pega una imagen con Ctrl+V)' : 'Write a note… (or paste an image with Ctrl+V)'}
            style={{
              flex: 1, width: '100%', resize: 'none', border: 'none', outline: 'none',
              background: 'transparent', color: c.text, padding: '8px 9px',
              fontSize: '.82rem', lineHeight: 1.4, fontFamily: 'var(--font-body, sans-serif)',
            }}
          />
        </div>
      )}

      {/* Resize handle */}
      {!collapsed && (
        <div onPointerDown={startResize}
          style={{ position: 'absolute', right: 0, bottom: 0, width: 16, height: 16, cursor: 'nwse-resize', touchAction: 'none',
            background: 'linear-gradient(135deg, transparent 50%, rgba(0,0,0,.28) 50%)', borderBottomRightRadius: 10 }} />
      )}
    </div>
  );
}

// Event name used to ask the overlay to drop a new note. The launcher lives in
// the app header (next to Deskte AI) and fires this; StickyNotes listens.
const ADD_EVENT = 'deskte:add-sticky-note';

// Header pill (matches .dkt-hpill) that creates a new sticky note. Kept separate
// from the overlay so it can sit in the header toolbar beside the Deskte AI
// button (Lissette 2026-07-12: "que no esté siempre visible... al lado de la
// Deskte AI").
export function StickyNoteButton({ lang }) {
  const es = lang === 'es';
  return (
    <button
      type="button"
      className="dkt-hpill"
      title={es ? 'Nueva nota' : 'New sticky note'}
      onClick={() => window.dispatchEvent(new Event(ADD_EVENT))}
    >
      <span style={{ fontSize: '.85rem' }}>📌</span>
    </button>
  );
}

export default function StickyNotes({ currentUser, lang }) {
  const es = lang === 'es';
  const key = `deskte_sticky_notes_${currentUser?.id || 'anon'}`;
  const [notes, setNotes] = useLocalStorage(key, []);

  const patchNote = (id, patch) => setNotes((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const closeNote = (id) => setNotes((prev) => prev.filter((x) => x.id !== id));

  // Add a note when the header launcher fires the event. Uses the functional
  // updater so the handler doesn't need to re-bind on every notes change.
  useEffect(() => {
    const add = () => setNotes((prev) => {
      const n = prev.length;
      return [
        ...prev,
        {
          id: genId(),
          text: '',
          image: null,
          color: COLORS[n % COLORS.length].id,
          x: Math.max(20, Math.min(window.innerWidth - 260, 120 + (n % 6) * 26)),
          y: Math.max(70, Math.min(window.innerHeight - 240, 90 + (n % 6) * 26)),
          w: 240,
          h: 200,
          collapsed: false,
        },
      ];
    });
    window.addEventListener(ADD_EVENT, add);
    return () => window.removeEventListener(ADD_EVENT, add);
  }, [setNotes]);

  return (
    <>
      {(notes || []).map((note) => (
        <Note key={note.id} note={note} es={es}
          onChange={(patch) => patchNote(note.id, patch)}
          onClose={() => closeNote(note.id)} />
      ))}
    </>
  );
}
